import { useMemo, useState } from "react";
import type { BuilderContext } from "common/utils/chatAgent/types";
import { EMPTY_FORM_STRUCTURE } from "common/utils/formStructure";
import { AssistantSuggestion } from "../../../../Submissions/ChatAgent/AssistantIntro";
import ChatPanel from "../../../../Submissions/ChatAgent/ChatPanel";
import ChatToggleButton from "../../../../Submissions/ChatAgent/ChatToggleButton";
import {
  BUILDER_THINKING_PHRASES,
  BUILDER_WORKING_PHRASES,
} from "../../../../Submissions/ChatAgent/ThinkingIndicator";
import BuilderToolActivityCard from "../../../../Submissions/ChatAgent/toolCards/BuilderToolActivityCard";
import { useBuilderFormContext } from "./form/form";

const BUILDER_SUGGESTIONS: AssistantSuggestion[] = [
  {
    text: "Draft a new damage assessment record with grouped sections for structure, utilities, and safety notes.",
    iconName: "component",
  },
  {
    text: "Add photo and document upload fields to capture site evidence for this form.",
    iconName: "upload",
  },
  {
    text: "Create dropdowns with standard compliance statuses and required follow-up actions.",
    iconName: "list",
  },
];

export const useSubmissionBuilderV2ChatAgent = () => {
  const [isOpen, setIsOpen] = useState(false);

  const ToggleButton = useMemo(() => {
    return (
      <ChatToggleButton onClick={() => setIsOpen(!isOpen)} isOpen={isOpen} />
    );
  }, [isOpen]);

  const { setValue, watch, trigger } = useBuilderFormContext();

  const watchedFormStructure = watch("formStructure");

  const builderContext: BuilderContext = useMemo(
    () => ({
      canCreateRolesAndGroupIds: watch("canCreateRolesAndGroupIds") ?? [],
      hiddenFromPublicByDefault: watch("hiddenFromPublicByDefault") ?? true,
    }),
    [watch],
  );

  const Panel = useMemo(() => {
    return (
      <ChatPanel
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        submissionTypeVersionId={undefined}
        formStructure={watchedFormStructure ?? EMPTY_FORM_STRUCTURE}
        api="/api/ai/chat/submissions-builder/stream"
        suggestions={BUILDER_SUGGESTIONS}
        showModelPicker
        ToolActivityCardComponent={BuilderToolActivityCard}
        thinkingPhrases={BUILDER_THINKING_PHRASES}
        workingPhrases={BUILDER_WORKING_PHRASES}
        builderContext={builderContext}
        onFormUpdate={(out: any) => {
          const next = out?.formStructure;
          if (!next) return;

          if (next.schema && next.uiSchema) {
            setValue("formStructure", next, { shouldDirty: true });
          }

          void trigger();
        }}
      />
    );
  }, [isOpen, watchedFormStructure, builderContext, setValue, trigger]);

  return {
    isOpen,
    setIsOpen,
    ChatToggleButton: ToggleButton,
    ChatPanel: Panel,
    isChatPanelOpen: isOpen,
  } as const;
};

export default useSubmissionBuilderV2ChatAgent;
